import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  TabletSmartphone, 
  Database, 
  Palette, 
  Brain, 
  MessageSquare, 
  Users, 
  FlaskConical, 
  Rocket,
  CheckCircle,
  ArrowRight,
  Lightbulb,
  TrendingUp
} from "lucide-react";

export default function HowWeBuiltIt() {
  const navigate = useNavigate();
  const [expandedChallenge, setExpandedChallenge] = useState(null);

  const techStack = [
    {
      name: "React Native + Expo",
      Icon: TabletSmartphone,
      description: "Cross-platform mobile development for iOS and Android with a single codebase",
      details: "Enables rapid iteration and consistent user experience across both platforms"
    },
    {
      name: "Firebase",
      Icon: Database,
      description: "Real-time backend infrastructure for authentication, database, and cloud functions",
      details: "Handles real-time reviews, user management, and scalable data storage"
    },
    {
      name: "Figma",
      Icon: Palette,
      description: "Professional UI/UX design system for intuitive mobile experiences",
      details: "Collaborative design process with rapid prototyping and user testing"
    }
  ];

  const challenges = [
    {
      title: "Reaching Chefs at Restaurants",
      Icon: MessageSquare,
      problem: "How do we ensure compliments actually reach the chef who made the dish?",
      solution: "Built a notification system that routes feedback directly to chefs via restaurant dashboards, with real-time alerts for new reviews",
      impact: "Chefs receive immediate recognition, creating authentic connections with diners"
    },
    {
      title: "Creating the Feedback Loop",
      Icon: Users,
      problem: "How do we build features that actually solve problems for chefs and diners?",
      solution: "Established direct communication channels with early-adopter chefs, conducting regular interviews and beta testing sessions",
      impact: "Product features are shaped by real chef needs, not assumptions"
    },
    {
      title: "Testing & Validation",
      Icon: FlaskConical,
      problem: "How do we validate the product before public launch?",
      solution: "Currently running closed testing on Google Play Store with select users, gathering feedback and iterating rapidly",
      impact: "Identifying bugs and UX issues in a controlled environment before scaling"
    }
  ];

  const roadmap = [
    {
      phase: "Done",
      title: "Closed Testing",
      Icon: FlaskConical,
      status: "COMPLETED",
      description: "Google Play Store closed testing with select user group",
      color: "text-gray-400",
      bgColor: "bg-gray-50",
      borderColor: "border-gray-200"
    },
    {
      phase: "Current",
      title: "App Store Launch",
      Icon: Rocket,
      status: "IN PROGRESS",
      description: "Expanding to iOS with App Store deployment",
      color: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200"
    },
    {
      phase: "Future",
      title: "NLP Sentiment Analysis",
      Icon: Brain,
      status: "PLANNED",
      description: "AI-powered emotion and sentiment extraction from reviews",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200"
    },
    {
      phase: "Future",
      title: "Data Analytics",
      Icon: TrendingUp,
      status: "PLANNED",
      description: "In-depth insights and analytics dashboard for restaurants and chefs",
      color: "text-orange",
      bgColor: "bg-orange/10",
      borderColor: "border-orange/30"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange/5">
      <div className="min-h-screen flex flex-col items-center p-4 md:p-8">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="max-w-6xl w-full"
        >
          {/* Header Section */}
          <div className="text-center mb-8 md:mb-16 mt-8">
            <motion.h1 
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className="text-3xl md:text-5xl font-bold text-gray-900 mb-3 md:mb-4 px-2"
            >
              How We <span className="text-orange">Built It</span>
            </motion.h1>
            <p className="text-base md:text-xl text-gray-700 max-w-2xl mx-auto px-4">
              The technology, challenges, and innovation behind Compliments to the Chef
            </p>
          </div>

          {/* Tech Stack Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-12 md:mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
              Our Tech Stack
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {techStack.map((tech, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white border-2 border-gray-200 rounded-xl p-6 md:p-8 shadow-lg hover:shadow-xl hover:border-orange transition-all"
                >
                  <tech.Icon className="w-10 h-10 md:w-12 md:h-12 mb-4 text-orange" />
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                    {tech.name}
                  </h3>
                  <p className="text-gray-700 text-sm md:text-base mb-3">
                    {tech.description}
                  </p>
                  <p className="text-gray-600 text-xs md:text-sm italic">
                    {tech.details}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Challenges Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mb-12 md:mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
              Challenges We Solved
            </h2>
            <div className="space-y-4 md:space-y-6">
              {challenges.map((challenge, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                  className={`bg-white border-2 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all ${
                    expandedChallenge === index ? 'border-orange' : 'border-gray-200'
                  }`}
                >
                  <div
                    className="p-6 md:p-8 cursor-pointer"
                    onClick={() => setExpandedChallenge(expandedChallenge === index ? null : index)}
                  >
                    <div className="flex items-start gap-4">
                      <challenge.Icon className="w-8 h-8 md:w-10 md:h-10 text-orange flex-shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg md:text-xl font-bold text-gray-900">
                            {challenge.title}
                          </h3>
                          <ArrowRight 
                            className={`w-5 h-5 text-orange transition-transform ${
                              expandedChallenge === index ? 'rotate-90' : ''
                            }`}
                          />
                        </div>
                        <div className="flex items-start gap-2 mb-3">
                          <Lightbulb className="w-4 h-4 text-gray-500 flex-shrink-0 mt-1" />
                          <p className="text-gray-700 text-sm md:text-base italic">
                            {challenge.problem}
                          </p>
                        </div>

                        {/* Expanded Content */}
                        {expandedChallenge === index && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            transition={{ duration: 0.3 }}
                            className="mt-4 pt-4 border-t border-gray-200"
                          >
                            <div className="flex items-start gap-2 mb-3">
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">
                                  Our Solution
                                </h4>
                                <p className="text-gray-700 text-sm md:text-base">
                                  {challenge.solution}
                                </p>
                              </div>
                            </div>
                            <div className="bg-orange/10 border-l-4 border-orange rounded-r p-3 md:p-4">
                              <h4 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">
                                Impact
                              </h4>
                              <p className="text-gray-700 text-sm md:text-base">
                                {challenge.impact}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Roadmap Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mb-12 md:mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
              Development Roadmap
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {roadmap.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1 + index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className={`${item.bgColor} border-2 ${item.borderColor} rounded-xl p-5 md:p-6 shadow-lg hover:shadow-xl transition-all`}
                >
                  <item.Icon className={`w-8 h-8 md:w-10 md:h-10 mb-3 ${item.color}`} />
                  <div className="text-xs font-bold text-gray-600 mb-1">{item.phase}</div>
                  <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <div className={`text-xs font-bold ${item.color} mb-3`}>
                    {item.status}
                  </div>
                  <p className="text-gray-700 text-xs md:text-sm">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Future Innovation Banner */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
            className="bg-gradient-to-r from-orange/20 to-orange/5 border-2 border-orange/50 rounded-xl p-6 md:p-8 shadow-lg mb-8 md:mb-12"
          >
            <div className="flex items-start gap-4">
              <Brain className="w-10 h-10 md:w-12 md:h-12 text-orange flex-shrink-0" />
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  What's Next: AI-Powered Insights
                </h3>
                <p className="text-gray-800 mb-4 text-sm md:text-base">
                  We're building the next generation of features that will transform how restaurants understand their customers:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-orange/30">
                    <h4 className="font-bold text-gray-900 mb-2 text-sm md:text-base">
                      NLP Sentiment Analysis
                    </h4>
                    <p className="text-gray-700 text-xs md:text-sm">
                      Extract emotions and sentiment from reviews automatically, helping chefs understand what diners truly feel
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-orange/30">
                    <h4 className="font-bold text-gray-900 mb-2 text-sm md:text-base">
                      Data Analytics Dashboard
                    </h4>
                    <p className="text-gray-700 text-xs md:text-sm">
                      Deep insights for restaurants - trending dishes, chef performance, customer preferences, and more
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="text-center bg-white border-2 border-gray-200 rounded-2xl p-8 md:p-12 shadow-2xl"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
              Want to Learn More?
            </h3>
            <p className="text-gray-700 mb-6 md:mb-8 max-w-2xl mx-auto text-sm md:text-lg px-4">
              Whether you're an investor interested in our technical approach, a partner looking to collaborate, or just curious about our journey - we'd love to connect.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
              <button 
                onClick={() => navigate("/contact")} 
                className="bg-orange text-white px-8 md:px-10 py-3 rounded-lg hover:bg-orange-dark transition duration-300 font-semibold text-base md:text-lg shadow-lg hover:shadow-xl w-full sm:w-auto"
              >
                Get in Touch
              </button>
              <button 
                onClick={() => window.open("https://www.complimentstothechef.co.uk/", "_blank")}
                className="border-2 border-orange text-orange px-8 md:px-10 py-3 rounded-lg hover:bg-orange hover:text-white transition duration-300 font-semibold text-base md:text-lg w-full sm:w-auto"
              >
                Try the Platform
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}